import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { AppState, AppAction } from './types';
import { appReducer, initialAppState } from './appReducer';

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialAppState);

  // Sync dark mode with DOM
  useEffect(() => {
    if (state.ui.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [state.ui.darkMode]);

  // Auto-remove toasts after their duration
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];

    state.toast.toasts.forEach(toast => {
      if (toast.duration && toast.duration > 0) {
        const timer = setTimeout(() => {
          dispatch({ type: 'REMOVE_TOAST', payload: toast.id });
        }, toast.duration);
        timers.push(timer);
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  }, [state.toast.toasts, dispatch]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// Convenience hooks for specific parts of state
export const useAppState = () => {
  const { state } = useAppContext();
  return state;
};

export const useAppDispatch = () => {
  const { dispatch } = useAppContext();
  return dispatch;
};

// Specific state selectors
export const useUIState = () => {
  const { state } = useAppContext();
  return state.ui;
};

export const useDataState = () => {
  const { state } = useAppContext();
  return state.data;
};

export const useSearchAndFilterState = () => {
  const { state } = useAppContext();
  return state.searchAndFilter;
};

export const useToastState = () => {
  const { state } = useAppContext();
  return state.toast;
};